import {tokenizeQuery, type QueryToken} from './query-tokenizer'

const STATE_FILTER_PREFIX = 'state:'

function isStateFilter(token: QueryToken): boolean {
  return (
    token.kind === 'term' &&
    token.value.startsWith(STATE_FILTER_PREFIX) &&
    token.value.length > STATE_FILTER_PREFIX.length
  )
}

export function addStateToQuery(query: string | undefined, state: string): string {
  const stateFilter = state ? `${STATE_FILTER_PREFIX}${state}` : ''

  if (!query) return stateFilter

  // Clearing the state is unreachable from the dashboards — the Open and Closed
  // shortcuts are the only callers and always name a state — so this keeps the
  // long-standing space-based removal rather than growing a Boolean-aware one.
  if (!stateFilter) {
    return query
      .split(' ')
      .filter(term => !term.startsWith(STATE_FILTER_PREFIX))
      .join(' ')
  }

  // Tokenize instead of splitting on spaces: in a nested Boolean query a state
  // qualifier can sit against a parenthesis, and `state:` text inside a quoted
  // value must not be mistaken for one.
  const tokens = tokenizeQuery(query, true)
  const stateTokens = tokens.filter(isStateFilter)

  if (stateTokens.length > 0) {
    // Each branch of a nested query carries its own state qualifier. Replacing
    // only the first left the others behind, producing queries like
    // `(is:pr AND AND archived:false)` that match nothing.
    for (const token of stateTokens) token.value = stateFilter
    return tokens.map(token => token.value).join('')
  }

  // Insert at the third term, matching the default query order. Tokenize again
  // without splitting parentheses so a group counts as one term, as it did when
  // this split on spaces — otherwise the qualifier lands inside the group and
  // would constrain only its first branch.
  const insertionTokens = tokenizeQuery(query)
  let termCount = 0
  const thirdTermIndex = insertionTokens.findIndex(token => token.kind === 'term' && ++termCount === 3)
  if (thirdTermIndex === -1) return `${query} ${stateFilter}`

  insertionTokens.splice(thirdTermIndex, 0, {kind: 'term', value: stateFilter}, {kind: 'separator', value: ' '})
  return insertionTokens.map(token => token.value).join('')
}

export function getStateFromQuery(query: string | undefined): string {
  if (!query) return ''

  const stateTerms = query.split(' ').filter(term => term.startsWith(STATE_FILTER_PREFIX))
  if (stateTerms.length === 0) return ''

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return stateTerms[0]!.substring(STATE_FILTER_PREFIX.length)
}
